import React, { useEffect, useRef } from 'react';
import { useController } from 'react-hook-form';
import tw from 'twin.macro';

import { Caption } from '@/components/ui/caption/caption';
import { SubBody } from '@/components/ui/sub-body/sub-body';

export const OtpInput = ({ control, name, label, disabled = false, rules, numInputs = 4, handleVerify = () => {} }) => {
  const {
    field,
    fieldState: { error, invalid }
  } = useController({ control, name, rules });

  const inputsRef = useRef([]);
  const hiddenRef = useRef(null);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  useEffect(() => {
    const el = hiddenRef.current;
    if (!el) return;
    const handleInput = e => {
      const code = e.target.value.replace(/\D/g, '').slice(0, numInputs);
      field.onChange(code);
      if (code.length === numInputs) {
        inputsRef.current[numInputs - 1]?.blur();
        handleVerify(code);
      }
    };
    el.addEventListener('input', handleInput);
    return () => el.removeEventListener('input', handleInput);
  }, [handleVerify, numInputs, field]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    const sanitizedValue = value.replace(/\D/g, '');

    if (sanitizedValue.length > 1) {
      const code = sanitizedValue.slice(0, numInputs);
      field.onChange(code);

      const lastFilledIndex = Math.min(code.length, numInputs - 1);
      inputsRef.current[lastFilledIndex]?.focus();

      if (code.length === numInputs) {
        inputsRef.current[lastFilledIndex]?.blur();
        handleVerify(code);
      }
    } else {
      const otpArray = field.value ? field.value.split('') : [];
      otpArray[index] = sanitizedValue;
      const newValue = otpArray.join('');
      field.onChange(newValue);

      if (sanitizedValue && index < numInputs - 1) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !field.value?.[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  return (
    <div css={[tw`flex flex-col space-y-1 min-w-0 w-full items-center`, disabled && tw`opacity-50`]}>
      {label && (
        <label htmlFor={name} tw='text-secondary'>
          <SubBody text={label} />
        </label>
      )}

      <input
        type='text'
        inputMode='numeric'
        autoComplete='one-time-code'
        ref={hiddenRef}
        tw='absolute opacity-0 pointer-events-none h-0 w-0'
        onInput={e => {
          const code = e.currentTarget.value.replace(/\D/g, '').slice(0, numInputs);
          if (code.length > 0) {
            field.onChange(code);
            if (code.length === numInputs) {
              inputsRef.current[numInputs - 1]?.blur();
              handleVerify(code);
            }
          }
        }}
      />

      <div tw='flex space-x-2 justify-center'>
        {Array.from({ length: numInputs }).map((_, index) => (
          <input
            key={index}
            type='tel'
            inputMode='numeric'
            value={field.value?.[index] || ''}
            disabled={disabled}
            onChange={e => handleChange(e, index)}
            onKeyDown={e => handleKeyDown(e, index)}
            ref={el => (inputsRef.current[index] = el)}
            css={[
              tw`border border-transparent outline-none text-primary font-bold bg-input w-[50px] h-[60px] rounded-2xl text-center text-xl`,
              tw`focus:border-input-active transition-colors`,
              invalid && tw`border-input-invalid focus:border-input-invalid`
            ]}
          />
        ))}
      </div>

      {invalid && (
        <span tw='leading-3 text-center'>
          <Caption text={error?.message} twStyle={tw`text-error relative text-center`} />
        </span>
      )}
    </div>
  );
};
